import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 20000,
});

// ---------- Global retry interceptor ----------
// Cloudflare returns 520-524 when its origin connection times out, drops, or
// returns malformed bytes. The origin pod recovers in <1s most of the time,
// so a silent retry is the right UX — we only surface the error to the user
// if BOTH attempts fail.
//
// Scope: only idempotent verbs (GET, HEAD). POSTs / DELETEs / PATCHes might
// have already taken effect on the server, so we never auto-retry those.
const RETRYABLE_STATUSES = new Set([502, 503, 504, 520, 521, 522, 523, 524]);
const RETRY_BACKOFFS_MS = [500, 1200, 3000]; // 3 retries + original = 4 tries, ~4.7s total

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const cfg = error?.config;
    if (!cfg || cfg.__retry_count >= RETRY_BACKOFFS_MS.length) throw error;
    const method = (cfg.method || "get").toLowerCase();
    if (method !== "get" && method !== "head") throw error;
    const status = error?.response?.status;
    const code = error?.code;
    const retryable =
      RETRYABLE_STATUSES.has(status) ||
      code === "ECONNABORTED" ||
      code === "ERR_NETWORK"; // axios surfaces Cloudflare-killed connections here
    if (!retryable) throw error;

    cfg.__retry_count = (cfg.__retry_count || 0) + 1;
    const wait = RETRY_BACKOFFS_MS[cfg.__retry_count - 1];
    await new Promise((r) => setTimeout(r, wait));
    return api.request(cfg);
  }
);

export async function fetchNearby({ lat, lng, radius, cuisine }) {
  const params = { lat, lng, radius };
  if (cuisine && cuisine !== "all") params.cuisine = cuisine;
  // The global interceptor above handles retries — we just call once now.
  const { data } = await api.get("/places/nearby", { params });
  return data;
}

export async function fetchPlaceDetails(placeId) {
  const { data } = await api.get(`/places/details/${placeId}`);
  return data;
}

export async function geocodeAddress(address) {
  const { data } = await api.get("/places/geocode", {
    params: { address },
  });
  return data;
}

export async function fetchAutocomplete({ input, lat, lng, restaurantsOnly = false, signal }) {
  const params = { input };
  if (lat != null && lng != null) {
    params.lat = lat;
    params.lng = lng;
  }
  if (restaurantsOnly) params.restaurants_only = true;
  const { data } = await api.get("/places/autocomplete", { params, signal });
  return data?.suggestions || [];
}

export async function resolvePlace(placeId) {
  const { data } = await api.get(`/places/resolve/${placeId}`);
  return data;
}
